import scrollTo from '../../common/scrollTo'
import PropTypes from 'prop-types'
import { useState, useEffect, useRef } from 'react'
import PetCard from './PetCard'
import CheckboxFilter from './CheckboxFilter'
import SelectFilter from './SelectFilter'
import FilterHeader from './FilterHeader'
import ToggleSidebarButton from './ToggleSidebarButton'

const initialFilters = {
  search: '',
  species: '',
  breed: '',
  age: '',
  gender: '',
  size: '',
  housetrained: '',
}

function App({ data }) {
  const initialLoad = useRef(true)
  const [searchTerm, setSearchTerm] = useState(initialFilters.search)
  const [searchingHousetrained, setSearchingHousetrained] = useState(initialFilters.housetrained !== '')
  const [isLoading, setIsLoading] = useState(false)
  const [isFiltered, setIsFiltered] = useState(false)
  const [isSidebarOpen, setIsSidebarOpen] = useState(false)
  const [filters, setFilters] = useState(initialFilters)
  const [resultsTotal, setResultsTotal] = useState(data.results_total)
  const [resultsHasMore, setResultsHasMore] = useState(data.results_has_more)
  const [resultsOffset, setResultsOffset] = useState(data.results_offset)
  const [pets, setPets] = useState(data.initial_pets)

  const handleLoadMore = () => {
    const url = new URL(`${data.endpoint}loadMore`)
    url.searchParams.append('offset', resultsOffset)

    for (const [key, value] of Object.entries(filters)) {
      url.searchParams.append(key, value)
    }

    setIsLoading(true)

    ajax
      .url(url.href)
      .get()
      .json((res) => {
        if (res.success) {
          setResultsOffset(res.results_offset)
          setResultsHasMore(res.results_has_more)
          setPets([...pets, ...res.pets])
        }
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleFilter = (name, value) => {
    setFilters((prevState) => ({
      ...prevState,
      [name]: value,
    }))
  }

  const handleSearch = (e) => {
    e.preventDefault()

    const term = e.target.search.value
    const newFilters = { ...initialFilters }

    newFilters['search'] = term

    setFilters(newFilters)
    setSearchingHousetrained(false)
  }

  const handleReset = () => {
    const newFilters = { ...initialFilters }

    setFilters(newFilters)
    setSearchTerm('')
    setSearchingHousetrained(false)
    setIsFiltered(false)
  }

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false
      return
    }

    const url = new URL(`${data.endpoint}fetchPets`)
    url.searchParams.append('offset', 0)

    for (const [key, value] of Object.entries(filters)) {
      url.searchParams.append(key, value)
    }

    setIsLoading(true)

    ajax
      .url(url.href)
      .get()
      .json((res) => {
        if (res.success) {
          setResultsOffset(res.results_offset)
          setResultsHasMore(res.results_has_more)
          setResultsTotal(res.results_total)
          setPets(res.pets)

          // Check if all filters are empty string
          setIsFiltered(!!Object.values(filters).some((x) => x !== null && x !== ''))
        }
      })
      .finally(() => {
        setIsLoading(false)

        if (window.matchMedia('(max-width: 767px)').matches) {
          setIsSidebarOpen(false)
        }

        scrollTo(
          document.querySelector('.adopt-pets-wrapper'),
          document.querySelector('.site-primary-header').offsetHeight
        )
      })
  }, [filters])

  return (
    <div className="adopt-pets-wrapper">
      <div className="flex items-center sm-down:hidden">
        <ToggleSidebarButton
          isSidebarOpen={isSidebarOpen}
          setIsSidebarOpen={setIsSidebarOpen}
          className="adopt-pets-sidebar-toggle"
        />
        <FilterHeader
          isVisible={isFiltered && pets.length > 0}
          filters={filters}
          handleReset={handleReset}
          className="pl-5 lg:pl-10"
        />
      </div>
      <div className="md:overflow-hidden">
        <aside className={`${isSidebarOpen ? '' : 'is-collapsed'} sm-down:w-full adopt-pets-sidebar`}>
          <div className="sm-down:-mx-5">
            <ToggleSidebarButton
              isSidebarOpen={isSidebarOpen}
              setIsSidebarOpen={setIsSidebarOpen}
              className="md:hidden"
            />

            <div
              className={`${isSidebarOpen ? 'block' : 'sm-down:hidden'} border border-t-0 rounded-b-lg bg-brand-beige`}
            >
              <form className="p-4 border-b border-gray-300" onSubmit={handleSearch}>
                <div className="relative">
                  <input
                    type="search"
                    name="search"
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value)
                    }}
                    placeholder="Search"
                    className="block w-full h-full p-3 px-4 pr-12 font-normal border border-gray-400 rounded-sm replace-focus hover:border-gray-600"
                  />
                  <button type="submit" className="absolute top-0 right-0 mt-3 mr-4 text-gray-400" aria-label="Search">
                    <svg viewBox="0 0 20 20" className="w-5 h-5">
                      <path
                        d="M18.98 19.863l.883-.883a.47.47 0 000-.664l-4.742-4.742a.464.464 0 00-.332-.137h-.516A8.124 8.124 0 008.125 0 8.124 8.124 0 000 8.125a8.124 8.124 0 0013.437 6.148v.516c0 .125.051.242.137.332l4.742 4.742a.47.47 0 00.664 0zM8.125 14.375a6.248 6.248 0 01-6.25-6.25 6.248 6.248 0 016.25-6.25 6.248 6.248 0 016.25 6.25 6.248 6.248 0 01-6.25 6.25z"
                        fill="currentColor"
                        fillRule="nonzero"
                      />
                    </svg>
                  </button>
                </div>
              </form>

              <form className="p-4 grid gap-4 styled-form">
                {data.filters.species.length && (
                  <SelectFilter
                    name="species"
                    value={filters.species}
                    options={data.filters.species}
                    placeholder="Species"
                    handleFilter={handleFilter}
                  />
                )}
                {data.filters.breed.length && (
                  <SelectFilter
                    name="breed"
                    value={filters.breed}
                    options={data.filters.breed}
                    placeholder="Breed"
                    handleFilter={handleFilter}
                  />
                )}
                {data.filters.age.length && (
                  <SelectFilter
                    name="age"
                    value={filters.age}
                    options={data.filters.age}
                    placeholder="Age"
                    handleFilter={handleFilter}
                  />
                )}
                {data.filters.gender.length && (
                  <SelectFilter
                    name="gender"
                    value={filters.gender}
                    options={data.filters.gender}
                    placeholder="Gender"
                    handleFilter={handleFilter}
                  />
                )}
                {data.filters.size.length && (
                  <SelectFilter
                    name="size"
                    value={filters.size}
                    options={data.filters.size}
                    placeholder="Size"
                    handleFilter={handleFilter}
                  />
                )}
                <div className="p-4">
                  <CheckboxFilter
                    name="housetrained"
                    label="Housetrained"
                    checked={searchingHousetrained}
                    handleFilter={handleFilter}
                    setSearchingHousetrained={setSearchingHousetrained}
                  />
                </div>
              </form>
            </div>
          </div>
        </aside>

        <div className={`relative pt-8 md:pt-4 adopt-pets-holder ${isSidebarOpen ? '' : 'is-expanded'}`}>
          <FilterHeader
            isVisible={isFiltered && pets.length > 0}
            filters={filters}
            handleReset={handleReset}
            className="pb-8 md:hidden"
          />

          {isLoading && (
            <div className="absolute inset-0 z-10 flex items-start justify-center pt-8 text-xl font-bold bg-white bg-opacity-80 font-brand"></div>
          )}

          {pets.length === 0 ? (
            <div className="text-center">
              <div className="text-2xl font-bold font-brand">No pets found.</div>
              <button type="button" className="mt-8 btn btn-fill btn-red" onClick={handleReset}>
                Try Another Search
              </button>
            </div>
          ) : (
            <>
              <div className="grid gap-y-10 md:gap-y-16 sm-down:grid-cols-2 gap-x-4 lg:gap-x-8 adopt-pets-grid">
                {pets.map((pet) => (
                  <PetCard key={pet.id} {...pet} />
                ))}
              </div>

              <div className="mt-16 md:mt-24">
                <div className="text-4xl text-center font-brand">
                  {pets.length.toString().padStart(2, '0')}/{resultsTotal.toString().padStart(2, '0')}
                </div>

                {resultsHasMore && (
                  <div className="mt-8 text-center">
                    <button type="button" className="btn btn-hollow btn-red" onClick={handleLoadMore}>
                      View More
                    </button>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  )
}

App.propTypes = {
  data: PropTypes.object,
}

export default App
